// Modální dialogy: potvrzení, dotaz na hodnotu, zpráva. Tlačítka vždy na stejném místě (vpravo, hlavní poslední).
import { h } from '../dom';
import { t } from '../i18n';
import { icon } from '../icons';

export interface DialogButton {
  id: string;
  label: string;
  primary?: boolean;
  danger?: boolean;
}

export interface DialogOptions {
  title?: string;
  message?: string | Node;
  buttons?: DialogButton[];
  input?: { label?: string; value?: string; placeholder?: string; type?: 'text' | 'password'; required?: boolean };
  icon?: string;
  width?: number;
}

export async function dialog(options: DialogOptions): Promise<{ id: string; value: string }> {
  return new Promise((resolve) => {
    const buttons = options.buttons ?? [{ id: 'ok', label: t('core.ui.ok'), primary: true }];
    const input = options.input
      ? h('input', { class: 'input', type: options.input.type ?? 'text', value: options.input.value ?? '', placeholder: options.input.placeholder ?? '', autocomplete: 'off' })
      : null;
    const dlg = h(
      'dialog',
      { class: 'iw-dialog', style: options.width ? { width: options.width + 'px' } : null },
      h(
        'form',
        { method: 'dialog' },
        options.title ? h('h2', { class: 'dialog-title' }, options.icon ? icon(options.icon) : null, options.title) : null,
        h(
          'div',
          { class: 'dialog-body' },
          options.message instanceof Node ? options.message : options.message ? h('p', null, options.message) : null,
          input ? h('label', { class: 'field' }, options.input?.label ? h('span', { class: 'field-label' }, options.input.label) : null, input) : null,
        ),
        h(
          'div',
          { class: 'dialog-buttons' },
          buttons.map((b) =>
            h(
              'button',
              { type: 'button', class: 'btn' + (b.primary ? ' primary' : '') + (b.danger ? ' danger' : ''), dataset: { id: b.id } },
              b.label,
            ),
          ),
        ),
      ),
    );
    const finish = (id: string) => {
      const value = input?.value ?? '';
      dlg.close();
      dlg.remove();
      resolve({ id, value });
    };
    dlg.querySelectorAll<HTMLButtonElement>('.dialog-buttons button').forEach((b) =>
      b.addEventListener('click', () => {
        if (b.dataset['id'] !== 'cancel' && options.input?.required && !(input?.value ?? '').trim()) {
          input?.focus();
          return;
        }
        finish(b.dataset['id'] ?? 'ok');
      }),
    );
    dlg.addEventListener('cancel', (e) => {
      if (e.target !== dlg) return; // 'cancel' z <input type=file> (zavřený výběr souboru) probublává – dialog nezavírat
      e.preventDefault();
      finish('cancel');
    });
    dlg.querySelector('form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const primary = buttons.find((b) => b.primary) ?? buttons[buttons.length - 1];
      if (primary) {
        if (options.input?.required && !(input?.value ?? '').trim()) return;
        finish(primary.id);
      }
    });
    // Enter v poli odešle dialog (hlavní tlačítko) i s více poli – formulář bez tlačítka „submit“ to sám neudělá
    dlg.addEventListener('keydown', (e) => {
      const el = e.target;
      if (e.key !== 'Enter' || e.shiftKey || e.isComposing || !(el instanceof HTMLInputElement) || ['checkbox', 'radio', 'button', 'file'].includes(el.type)) return;
      e.preventDefault();
      dlg.querySelector('form')?.requestSubmit();
    });
    document.body.appendChild(dlg);
    dlg.showModal();
    // fokus do prvního pole (heslo v ověření…), jinak na hlavní tlačítko; u nevratné akce (smazat) na Zrušit – Enter nic nesmaže
    const safe = buttons.some((b) => b.danger) ? dlg.querySelector<HTMLElement>('button[data-id="cancel"]') : null;
    (input ?? dlg.querySelector<HTMLElement>('.dialog-body input:not([type=hidden]), .dialog-body select, .dialog-body textarea') ?? safe ?? dlg.querySelector<HTMLElement>('button.primary') ?? dlg.querySelector<HTMLElement>('button'))?.focus();
    input?.select();
  });
}

export async function confirm(message: string, options: { title?: string; danger?: boolean; okLabel?: string; cancelLabel?: string } = {}): Promise<boolean> {
  const r = await dialog({
    title: options.title,
    message,
    icon: options.danger ? 'warning' : undefined,
    buttons: [
      { id: 'cancel', label: options.cancelLabel ?? t('core.ui.cancel') },
      { id: 'ok', label: options.okLabel ?? t('core.ui.ok'), primary: true, danger: options.danger },
    ],
  });
  return r.id === 'ok';
}

/** Smazat? (nebezpečné: fokus na Storno, tlačítko „Smazat“) – jedno místo pro všechna mazání (kontrola 3. 11.). */
export function confirmDelete(message?: string, count = 1): Promise<boolean> {
  return confirm(message ?? t('core.cmd.confirm_delete', { count }), { danger: true, okLabel: t('core.cmd.delete'), title: t('core.ui.confirm_delete_title') });
}

/** Zahodit neuložené změny? (fokus na „Pokračovat v úpravách“) */
export function confirmDiscard(message?: string): Promise<boolean> {
  return confirm(message ?? t('core.ui.unsaved'), { danger: true, okLabel: t('core.ui.discard'), cancelLabel: t('core.ui.keep_editing'), title: t('core.ui.unsaved_title') });
}

export async function prompt(label: string, value = '', options: { title?: string; type?: 'text' | 'password' } = {}): Promise<string | null> {
  const r = await dialog({
    title: options.title ?? label,
    input: { label: options.title ? label : undefined, value, type: options.type ?? 'text', required: true },
    buttons: [
      { id: 'cancel', label: t('core.ui.cancel') },
      { id: 'ok', label: t('core.ui.ok'), primary: true },
    ],
  });
  return r.id === 'ok' ? r.value.trim() : null;
}

export async function alert(message: string, title?: string): Promise<void> {
  await dialog({ title, message });
}
